import {useQuery} from '@tanstack/react-query'
import {useAuthUser} from 'react-auth-kit'
import {useNavigate, useParams} from 'react-router-dom'
import {toast} from 'react-toastify'
import {API} from 'src/feature/bite/domain'
import {getTimeZone} from 'src/feature/bite/helpers/date'
import useRequestParams from 'src/feature/bite/hook/request'
import {useGetDetailTermsAndConditions} from 'src/feature/store/context/useQuery'
import {ItemCategoryListSchemaResponse, ItemListSchemaResponse, ItemSchemaResponse} from '../domain'
import {useItem} from '../hook'

export function useGetActiveList() {
  const {auth, headers, errorMessage, signOut} = useRequestParams()
  const {listActiveFilter, listActiveSet} = useItem()

  const userID = auth()?.id

  return useQuery(
    ['ItemActiveList', listActiveFilter],
    async () => {
      const Response = await fetch(`${API}/Item/GetItemList`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...listActiveFilter,
          userID,
          language: headers.Language,
        }),
      })

      if (Response.status === 401) {
        signOut()
      }

      const Data = await Response.json()

      const safeParse = ItemListSchemaResponse.safeParse(Data)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = ItemListSchemaResponse.parse(Data)

      const filteredByPaymentLinkEnable = parse.response.dataList.filter(
        (item) => item.paymentLinkEnable === true
      )

      listActiveSet({
        ...parse.response,
        dataList: filteredByPaymentLinkEnable,
      })

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetActiveListNotPackage() {
  const {auth, headers, errorMessage, signOut} = useRequestParams()
  const {listActiveFilterNotPackage, listActiveSet} = useItem()

  const userID = auth()?.id

  return useQuery(
    ['ItemActiveList', listActiveFilterNotPackage],
    async () => {
      const Response = await fetch(`${API}/Item/GetItemList`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...listActiveFilterNotPackage,
          userID,
          language: headers.Language,
        }),
      })

      if (Response.status === 401) {
        signOut()
      }

      const Data = await Response.json()

      const safeParse = ItemListSchemaResponse.safeParse(Data)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = ItemListSchemaResponse.parse(Data)

      listActiveSet(parse.response)

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetList() {
  const {auth, headers, errorMessage, signOut} = useRequestParams()
  const {listFilter, listSet} = useItem()

  const userID = auth()?.id

  return useQuery(
    ['ItemList', listFilter],
    async () => {
      const Response = await fetch(`${API}/Item/GetItemList`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...listFilter,
          userID,
          language: headers.Language,
        }),
      })

      if (Response.status === 401) {
        signOut()
      }

      const Data = await Response.json()

      const safeParse = ItemListSchemaResponse.safeParse(Data)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = ItemListSchemaResponse.parse(Data)

      listSet(parse.response)

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetItemTypeList() {
  const router = useNavigate()
  const {auth, headers, errorMessage, signOut} = useRequestParams()

  const userID = auth()?.id

  return useQuery(
    ['ItemTypeList'],
    async () => {
      const Response = await fetch(`${API}/Item/GetItemTypeList`, {
        method: 'POST',
        headers,
        body: JSON.stringify({userID, language: headers.Language}),
      })

      if (Response.status === 401) {
        signOut()
      }

      const Data = await Response.json()

      let item = Data.response?.dataList

      return item
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onError: (error) => {
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetItemCategoryList() {
  const {auth, headers, errorMessage, signOut} = useRequestParams()
  const {categoriesSet} = useItem()

  const userID = auth()?.id

  return useQuery(
    ['ItemCategoryList'],
    async () => {
      const Response = await fetch(`${API}/Item/GetItemCategoryList`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          userID,
          language: headers.Language,
        }),
      })

      if (Response.status === 401) {
        signOut()
      }

      const Data = await Response.json()

      const safeParse = ItemCategoryListSchemaResponse.safeParse(Data)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = ItemCategoryListSchemaResponse.parse(Data)

      categoriesSet(parse.response.dataList)

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetItemSubCategoryList() {
  const {auth, headers, errorMessage, signOut} = useRequestParams()
  const {detail, subCategoriesSet} = useItem()

  const userID = auth()?.id

  return useQuery(
    ['ItemSubCategoryList', detail?.categoryID],
    async () => {
      const Response = await fetch(`${API}/Item/GetItemSubCategoryList`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          userID,
          language: headers.Language,
          categoryIDs: [detail?.categoryID],
        }),
      })

      if (Response.status === 401) {
        signOut()
      }

      const Data = await Response.json()

      const safeParse = ItemCategoryListSchemaResponse.safeParse(Data)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = ItemCategoryListSchemaResponse.parse(Data)

      subCategoriesSet(parse.response.dataList)

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetDetail({itemIdVal}: {itemIdVal?: string} = {itemIdVal: ''}) {
  const router = useNavigate()
  const {auth, headers, errorMessage, signOut} = useRequestParams()
  const {detailSet} = useItem()
  const {itemId} = useParams()
  const {data: isTermsAndConditionsAvailable, isLoading: isLoadingTermsAvailable} =
    useGetDetailTermsAndConditions()

  const userID = auth()?.id
  const authData = useAuthUser()

  return useQuery(
    ['ItemDetail', itemId, itemIdVal],
    async () => {
      if (!itemId) return true
      const timeZoneOffset = getTimeZone()

      const Response = await fetch(`${API}/Item/GetItem`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          id: itemIdVal || itemId,
          userID,
          language: headers.Language,
          timeZoneOffset,
        }),
      })

      if (Response.status === 401) {
        signOut()
      }

      const Data = await Response.json()

      const safeParse = ItemSchemaResponse.safeParse(Data)
      const parse = ItemSchemaResponse.parse(Data)

      if (!parse.response) {
        router('/error/items')
      }

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      let item = parse.response

      // Start: Get HTML Items
      if (item?.infoContentData) {
        const faqs = [
          {
            title: 'Feature and Benefits',
          },
          {
            title: 'Eligibility',
          },
          {
            title: 'Additional Benefits',
          },
          {
            title: 'Terms and Conditions',
          },
        ]

        await Promise.all(
          item?.infoContentData?.map(async (item) => {
            const dataUrl = item?.contentURL
            if (dataUrl) {
              const res = await fetch(dataUrl, {
                method: 'GET',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${auth()?.token}`,
                },
              })
              const dataValue = await res.text()
              const parser = new DOMParser()
              const doc = parser.parseFromString(dataValue, 'text/html')
              const contentView = doc.querySelector('#contentView')

              if (contentView) {
                item.data = contentView.innerHTML || ''
              }
            }
            return item
          })
        )

        if (item?.termsAndConditions && isTermsAndConditionsAvailable) {
          const termsUrl = item?.termsAndConditions
          if (termsUrl) {
            const res = await fetch(termsUrl, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${auth()?.token}`,
              },
            })

            const dataValue = await res.text()
            const parser = new DOMParser()
            const doc = parser.parseFromString(dataValue, 'text/html')
            const contentView = doc.querySelector('#contentView')

            if (contentView) {
              item.termsAndConditions = contentView.innerHTML || ''
            } else item.termsAndConditions = ''
          }

          item?.infoContentData?.splice(3, 0, {
            name: 'Terms and Conditions',
            data: item?.termsAndConditions || '',
          })
        }

        await Promise.all(
          faqs.map((faq) => {
            const itemEl = item?.infoContentData?.find((item) => item.name === faq.title)
            if (!itemEl) {
              item?.infoContentData?.push({
                name: faq.title,
                data: '',
              })
            }
          })
        )
      }

      // End: Get HTML Items

      if (item?.detail) {
        const dataUrl = item?.detail

        if (dataUrl) {
          const res = await fetch(dataUrl, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${auth()?.token}`,
            },
          })

          const dataValue = await res.text()
          const parser = new DOMParser()
          const doc = parser.parseFromString(dataValue, 'text/html')
          const contentView = doc.querySelector('#contentView')

          if (contentView) {
            item.detail = contentView.innerHTML || ''
          } else item.detail = ''
        }
      }

      detailSet(item)

      return true
    },
    {
      enabled: !isLoadingTermsAvailable,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onError: (error) => {
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}
